/** Master-P3 Wave 8.3 — OAuth Account-Selection Modal.
 *
 *  Rendered by the Onboarding route when the OAuth callback URL
 *  carries ``?needs_account_selection=1&connection_id=<id>&platform=<p>``.
 *  Fetches the ad-account list captured during callback, lets the
 *  user pick one-or-many, POSTs to
 *  ``/api/v1/oauth/{platform}/select-accounts``, and closes.
 *
 *  Contract:
 *  - No automatic "select all". Empty selection → server 400 →
 *    surfaced as an inline error.
 *  - Modal is dismissable via Escape or backdrop click, in which
 *    case the connection remains ``status=pending_account_selection``
 *    (user can retry from Settings later).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";

const I18N = {
  de: {
    title: "Werbekonten anschließen",
    subtitle: "Wähle die Ad-Konten aus, die VIONA verwalten soll.",
    empty: "Keine Ad-Konten gefunden. Prüfe die Provider-Berechtigungen.",
    loading: "Werbekonten werden geladen …",
    cancel: "Später",
    confirm: "Anschließen",
    persisted: "%n Werbekonto(en) angeschlossen.",
    error: "Auswahl konnte nicht gespeichert werden.",
    mocked: "Vorschau-Modus — verwendet Test-Konten.",
  },
  en: {
    title: "Attach ad accounts",
    subtitle: "Pick the ad accounts VIONA should manage.",
    empty: "No ad accounts found. Check provider permissions.",
    loading: "Loading ad accounts …",
    cancel: "Later",
    confirm: "Attach",
    persisted: "%n ad account(s) attached.",
    error: "Could not save the selection.",
    mocked: "Preview mode — using mock accounts.",
  },
};

export default function OAuthAccountSelection({
  open, platform, connectionId, lang = "de", onClose, onSuccess,
}) {
  const t = I18N[lang] || I18N.de;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [mocked, setMocked] = useState(false);
  const [checked, setChecked] = useState(() => new Set());
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !connectionId || !platform) return;
    setLoading(true);
    setErr(null);
    api.get(`/oauth/${platform}/available-accounts`, {
      params: { connection_id: connectionId },
    })
      .then((r) => {
        setRows(r.data?.available_accounts || []);
        setMocked(!!r.data?.mocked);
      })
      .catch((e) => setErr(e?.response?.data?.detail?.error || String(e)))
      .finally(() => setLoading(false));
  }, [open, connectionId, platform]);

  const toggle = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selected = useMemo(() => Array.from(checked), [checked]);
  const submitDisabled = saving || selected.length === 0;

  async function submit() {
    if (selected.length === 0) return;
    setSaving(true);
    setErr(null);
    try {
      const r = await api.post(`/oauth/${platform}/select-accounts`, {
        connection_id: connectionId,
        selected_account_ids: selected,
      });
      toast.success(t.persisted.replace("%n", String(r.data?.persisted || selected.length)));
      onSuccess?.(r.data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail?.error || String(e);
      setErr(msg);
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg"
                       data-testid="oauth-account-selection-modal">
        <DialogHeader>
          <DialogTitle data-testid="oauth-account-selection-title">
            {t.title}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {t.subtitle}
          </p>
        </DialogHeader>
        {mocked && (
          <div className="text-xs text-amber-800 bg-amber-50 rounded-md p-2 mb-3"
                  data-testid="oauth-account-selection-mocked-badge">
            {t.mocked}
          </div>
        )}
        <div className="space-y-2 max-h-96 overflow-auto">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"
                    data-testid="oauth-account-selection-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t.loading}
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-muted-foreground"
                    data-testid="oauth-account-selection-empty">
              {t.empty}
            </div>
          )}
          {!loading && rows.map((row) => (
            <label
              key={row.external_account_id}
              className="flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-slate-50"
              data-testid={`oauth-account-row-${row.external_account_id}`}>
              <Checkbox
                checked={checked.has(row.external_account_id)}
                onCheckedChange={() => toggle(row.external_account_id)}
                data-testid={`oauth-account-checkbox-${row.external_account_id}`} />
              <div className="flex-1 text-sm">
                <div className="font-medium">{row.name}</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {row.external_account_id} · {row.currency || "—"}
                </div>
              </div>
              {checked.has(row.external_account_id) && (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              )}
            </label>
          ))}
        </div>
        {err && (
          <div className="text-xs text-rose-700 mt-2"
                  data-testid="oauth-account-selection-error">
            {err}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                     data-testid="oauth-account-selection-cancel">
            {t.cancel}
          </Button>
          <Button onClick={submit} disabled={submitDisabled}
                     data-testid="oauth-account-selection-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
